import {Mapper} from '@s-ui/domain'
import {slugify} from '@s-ui/js/lib/string'

const PAGE_TYPE = {
  SIMILAR_ADS: 'similar-ads',
  ERROR: 'error',
  DEALER_RATINGS_MODAL: 'dealer-ratings-modal'
}
const DEFAULT_TRACKING_EVENT = 'Detail Viewed'

/**
 * @implements Mapper
 */
export default class TrackingAdsDetailMapper extends Mapper {
  /**
   * @constructor
   * @param {Object} param
   * @param {Object} param.config
   * @param {Object} param.fromPageTrackingJsonToTrackingDataValueObjectMapper
   * @param {Object} param.fromPageTrackingJsonToAdvertisementsValueObjectMapper
   * @param {Function} param.trackingDataObjectMapper
   */
  constructor({
    config,
    fromPageTrackingJsonToTrackingDataValueObjectMapper,
    fromPageTrackingJsonToAdvertisementsValueObjectMapper,
    trackingDataObjectMapper
  }) {
    super()

    const {HAS_FINANCING_PRI_ENABLED, RESERVATION_STATUSES} = config.get('financing')
    const {OFFER_TYPE_SECOND_HAND, OFFER_TYPE_SUBSCRIPTION} = config.get('search')
    const {SELLER_TYPE_PROFESSIONAL, SELLER_TYPE_PRIVATE} = config.get('dealer')
    const {
      SELLER_TYPES_TRANSLATIONS,
      BODY_TYPES_TRANSLATIONS,
      TRACKING_FUEL_TYPES_FILTERS_TRANSLATIONS,
      OFFER_TYPES_TRANSLATIONS,
      CATEGORIES_LEVEL_1_TRANSLATIONS,
      CATEGORIES_LEVEL_2_TRANSLATIONS
    } = config.get('tracking')

    this._config = config
    this._culture = this._config.get('DEFAULT_CULTURE')
    this._isCoches = this._config.get('APP_NAME') === this._config.get('APP_NAME_COCHES')
    this._showWarranty = this._config.get('search').SHOW_WARRANTY
    this._offerTypeSecondHand = OFFER_TYPE_SECOND_HAND
    this._offerTypeSubscription = OFFER_TYPE_SUBSCRIPTION
    this._sellerTypeProfessional = SELLER_TYPE_PROFESSIONAL
    this._sellerTypePrivate = SELLER_TYPE_PRIVATE
    this._sellerTypesTranslations = SELLER_TYPES_TRANSLATIONS
    this._bodyTypesTranslations = BODY_TYPES_TRANSLATIONS
    this._fuelTypesTranslations = TRACKING_FUEL_TYPES_FILTERS_TRANSLATIONS
    this._hasFinancingPriEnabled = HAS_FINANCING_PRI_ENABLED
    this._offerTypesTranslations = OFFER_TYPES_TRANSLATIONS
    this._categoryLevel1Translations = CATEGORIES_LEVEL_1_TRANSLATIONS
    this._categoryLevel2Translations = CATEGORIES_LEVEL_2_TRANSLATIONS
    this._fromPageTrackingJsonToTrackingDataValueObjectMapper = fromPageTrackingJsonToTrackingDataValueObjectMapper
    this._fromPageTrackingJsonToAdvertisementsValueObjectMapper = fromPageTrackingJsonToAdvertisementsValueObjectMapper
    this._reservationStatuses = RESERVATION_STATUSES
    this._trackingDataObjectMapper = trackingDataObjectMapper
  }

  _getProductsFrom({advertisements, adsTypeName, extendedProduct, listType, context}) {
    const isDetail = context === 'detail'

    const defaultDetailProperties = {
      vehicleHistory: false
    }

    const ads = advertisements.map(ad => ({
      ...(isDetail && defaultDetailProperties),
      ...ad,
      price: 0,
      price_indicator: ad.priceRankIndicator,
      quantity: 0
    }))

    const {products} = this._fromPageTrackingJsonToAdvertisementsValueObjectMapper
      .setParams({adsTypeName, listType, context})
      .map({ads, extendedProduct})
      .toJSON()

    return products.length ? products : undefined
  }

  _getProductsTypeName({isSimilarAds, isErrorPopup}) {
    if (isErrorPopup) return
    return isSimilarAds ? 'ad_impression' : 'ad_view'
  }

  /**
   * @method
   * @private
   */
  _parseAds = ({ad = {}, similarAds = []}) => {
    const ads = similarAds.map(id => ({id}))

    if (ad.id) ads.unshift({eventName: 'ad_view', ...ad})

    return ads
  }

  _getAdHasInstalment = instalmentPrice =>
    instalmentPrice !== null && instalmentPrice !== undefined ? true : undefined

  _getPriceDrop = priceDropData => {
    const {amount: priceDropAmount} = priceDropData

    const PRICE_DROP_VALUE_DEFAULT = 0
    return priceDropAmount > 0 ? priceDropAmount : PRICE_DROP_VALUE_DEFAULT
  }

  _mapDetailData(adDetail) {
    const {SCARCITY} = this._config.get('COMMON')
    const {TRACKING_HAS_PRICEDROP_TRUE, TRACKING_HAS_PRICEDROP_FALSE} = this._config.get('tracking')
    const {SCARCITY_TYPE_NONE, SCARCITY_TRACKING_INFO_MAP} = SCARCITY

    const {
      ad,
      adType,
      filteredProvinces,
      filtersString,
      financingInstalmentPrice,
      isTradableaAd,
      priceDropData,
      seller,
      transactionInfo,
      vehicleInfo
    } = adDetail
    const sellerTypeId = seller.isDealer ? this._sellerTypeProfessional : this._sellerTypePrivate
    const {offerTypeId} = adType
    const hasRating = Boolean(seller.dealer.ratings?.average)
    const ratingValue = seller.dealer.ratings?.average
    const hasPriceDropData = Boolean(priceDropData)
    const hasGenericPriceDrop = Boolean(ad.priceDrop)
    const hasFilters = Boolean(filtersString)

    const {scarcityType = SCARCITY_TYPE_NONE} = ad

    return {
      bodyTypes: this._bodyTypesTranslations[ad.bodyTypeId],
      bodyTypesId: ad.bodyTypeId && String(ad.bodyTypeId),
      brand: vehicleInfo.specifications.make,
      brandId: ad.makeId,
      certification: ad.isCertified,
      colors: ad.color,
      doors: vehicleInfo.specifications.doors && String(vehicleInfo.specifications.doors),
      ecoLabel: ad.environmentalLabel,
      financed: ad.isFinanced,
      ...(hasFilters && {filtersString}),
      filtered_provinces: filteredProvinces || this._config.get('filteredProvincesResults').UNKNOWN,
      fuelTypes: this._fuelTypesTranslations[ad.fuelTypeId],
      fuelTypesId: ad.fuelTypeId && String(ad.fuelTypeId),
      has_instalment: this._getAdHasInstalment(financingInstalmentPrice),
      has_online_financing: transactionInfo?.hasOnlineFinancing,
      has_reservation: transactionInfo?.reservationStatus === this._reservationStatuses?.RESERVABLE || undefined,
      ...(this._hasFinancingPriEnabled && {has_paycar: Boolean(isTradableaAd)}),
      has_pricedrop: hasGenericPriceDrop ? TRACKING_HAS_PRICEDROP_TRUE : TRACKING_HAS_PRICEDROP_FALSE,
      has_scarcity: SCARCITY_TRACKING_INFO_MAP[scarcityType],
      id: ad.id,
      instalment_price: financingInstalmentPrice,
      km: ad.km,
      model: vehicleInfo.specifications.model,
      modelId: ad.modelId,
      // because of compatibility issues with Braze, we need to send offer_type as "suscripcion" instead of "suscripción"
      // we leave the other offer types as they are, not to cause side effects
      offerType:
        offerTypeId === this._offerTypeSubscription
          ? slugify(this._offerTypesTranslations[offerTypeId][this._culture])
          : this._offerTypesTranslations[offerTypeId],
      power: vehicleInfo.specifications.hp,
      location: ad.location,
      price: ad.price || ad.monthlySubscriptionFee,
      ...(hasPriceDropData && {pricedrop: this._getPriceDrop(priceDropData)}),
      priceRankIndicator: ad.priceRankIndicator,
      provinces: ad.province,
      provincesId: ad.provinceId.toString(),
      ...(hasRating && {ratingValue}),
      seats: vehicleInfo.specifications.seats && String(vehicleInfo.specifications.seats),
      sellerType: this._sellerTypesTranslations[sellerTypeId],
      sellerTypeId,
      statusCar: this.getStatusCar({adType}),
      transmission: ad.transmissionType,
      transmissionId: ad.transmissionTypeId,
      version: vehicleInfo.specifications.version,
      ...(this._showWarranty && {warranty: ad.hasWarranty}),
      year: ad.year
    }
  }

  getStatusCar = ({adType: {offerTypeId, category2Id}}) => {
    const hasStatusFromCategory =
      offerTypeId === this._offerTypeSecondHand || offerTypeId === this._offerTypeSubscription
    return hasStatusFromCategory
      ? this._categoryLevel2Translations[category2Id][this._culture]
      : this._offerTypesTranslations[offerTypeId][this._culture]
  }

  getTrackingData = data => {
    const {
      ADS_DETAIL_PAGE_TYPE,
      LIST_VIEW_EVENT,
      PAGE_VIEW_EVENT,
      TRACKING_TRANSLATIONS_BY_CULTURE,
      RATINGS_VIEWED_EVENTS
    } = this._config.get('tracking')

    const {ad, adDetail, category, contactType, pageType, ratings, similarAds} = data

    const {product: extendedProduct} = adDetail || {}

    const hasNewAdContract = Boolean(adDetail)

    const TRACKING_LITERALS = TRACKING_TRANSLATIONS_BY_CULTURE[this._culture]

    const {
      id,
      price,
      highlightType,
      location,
      priceRankIndicator,
      vehicleHistory = false,
      ...filterData
    } = hasNewAdContract ? this._mapDetailData(adDetail) : ad

    const isDealerRatingsModal = pageType === PAGE_TYPE.DEALER_RATINGS_MODAL
    const isSimilarAds = pageType === PAGE_TYPE.SIMILAR_ADS
    const isErrorPopup = pageType === PAGE_TYPE.ERROR

    const advertisements = this._parseAds({
      ...(isSimilarAds === false && {
        ad: {id, price, highlightType, vehicleHistory, priceRankIndicator, location}
      }),
      similarAds
    })

    const adsTypeName = this._getProductsTypeName({isSimilarAds, isErrorPopup})

    const products = this._getProductsFrom({
      advertisements,
      adsTypeName,
      context: isSimilarAds ? 'list' : 'detail',
      extendedProduct,
      listType: isSimilarAds ? TRACKING_LITERALS.ADS_DETAIL_RELATED_ADS_POPUP_LIST_TYPE : undefined
    })

    const vehicleProperties = this._trackingDataObjectMapper.map({
      price,
      ad_id: id,
      ...filterData
    })

    const universalCategory = hasNewAdContract
      ? this._categoryLevel1Translations[adDetail.adType.category1Id][this._culture].toLowerCase()
      : category

    if (isErrorPopup) {
      return {
        page_name: TRACKING_LITERALS.ADS_DETAIL_ERROR_PAGE_NAME,
        page_type: 'popup',
        page_category_level1: 'error',
        event: PAGE_VIEW_EVENT,
        products
      }
    }

    if (isSimilarAds) {
      return {
        page_name: TRACKING_LITERALS.ADS_DETAIL_RELATED_ADS_POPUP_PAGE_NAME,
        page_type: 'popup',
        page_category_level1: universalCategory,
        event: LIST_VIEW_EVENT,
        list_type: TRACKING_LITERALS.ADS_DETAIL_RELATED_ADS_POPUP_LIST_TYPE,
        products,
        page_category_level2:
          contactType === 'phoneCall'
            ? TRACKING_LITERALS.ADS_DETAIL_RELATED_ADS_POPUP_CONTACT_TYPE_CALL
            : TRACKING_LITERALS.ADS_DETAIL_RELATED_ADS_POPUP_CONTACT_TYPE_MAIL
      }
    }

    if (isDealerRatingsModal) {
      const ratingsValueWithOnlyOneDecimal = Number(ratings.average?.toFixed(1))

      return {
        page_name: TRACKING_LITERALS.ADS_DETAIL_PAGE_NAME,
        event: RATINGS_VIEWED_EVENTS,
        hit_information: ratings.totalReviews || 0,
        rating_value: ratingsValueWithOnlyOneDecimal || 0
      }
    }

    return {
      page_name: TRACKING_LITERALS.ADS_DETAIL_PAGE_NAME,
      page_type: ADS_DETAIL_PAGE_TYPE,
      page_category_level1: universalCategory,
      event: DEFAULT_TRACKING_EVENT,
      products,
      ...vehicleProperties
    }
  }

  /**
   * @param {Object} param
   * @param {String} param.page
   * @param {String} param.culture
   * @param {Object} param.data
   * @param {String} param.data.pageType
   * @param {String} param.data.category
   * @param {Object} param.data.ad
   * @param {Object} param.data.similarAds
   * @param {Object} param.data.contactType
   */
  map({page, culture, data}) {
    return this._fromPageTrackingJsonToTrackingDataValueObjectMapper
      .setParams({page, culture})
      .map(this.getTrackingData(data))
  }
}
